import React from 'react';
import { colorPalette } from '@sharefiledev/antd-config';
import { Check, Template, Word } from '@sharefiledev/icons';
import { EntitlementsContextApi, PiletApi } from '@sharefiledev/sharefile-appshell';
import {
	Alert,
	Avatar,
	Card,
	Col,
	Flex,
	GlobalToken,
	Row,
	theme,
	Typography,
} from 'antd';
import { useAppStore } from '../../common/store';
import { getAnalyticsIdForTemplateCard } from '../../common/tracking/analyticsUtils';
import { DocGenExtensionSlots, DocumentTemplateTypes } from '../../constants';
import { t } from '../../utils';
import { isCreateEditableWordTemplateActionAvailable } from '../../utils/access';
import { openCreateTemplateDrawer } from '../CreateTemplate';

const { Text } = Typography;
const TEMPLATE_ICON_SIZE = 20;

type TemplateType = {
	icon: React.ElementType;
	title: string;
	description: React.ReactNode;
	onClick: () => void;
	showAlert?: boolean;
	alertMessage?: string;
};

export const getTemplateTypes = (entitlements: EntitlementsContextApi) => [
	{
		icon: Template,
		title: DocumentTemplateTypes.Static.name,
		description: (token: GlobalToken) => (
			<Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
				{t('createDocumentTemplateModal.templates.dynamic.description', {
					lineBreak: <br />,
				})}
			</Text>
		),
		onClick: 'handleCreatePDFTemplate',
		isAvailable: true,
	},
	{
		icon: Word,
		title: DocumentTemplateTypes.Dynamic.name,
		description: (token: GlobalToken) => (
			<Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
				{t('createDocumentTemplateModal.templates.editableWord.description', {
					lineBreak: <br />,
				})}
			</Text>
		),
		showAlert: true,
		getAlertMessage: () =>
			t('createDocumentTemplateModal.templates.editableWord.alertMessage'),
		onClick: 'handleCreateEditableWordTemplate',
		isAvailable: isCreateEditableWordTemplateActionAvailable(entitlements),
	},
];

export const FEATURES = {
	addFields: 'createDocumentTemplateModal.features.addFields',
	systemFilledData: 'createDocumentTemplateModal.features.systemFilledData',
};

const FeatureList: React.FC = React.memo(() => {
	const { token } = theme.useToken();
	return (
		<div style={{ marginTop: token.marginSM, marginBottom: token.marginLG }}>
			{Object.values(FEATURES).map(feature => (
				<Flex
					key={feature}
					align="center"
					gap={8}
					style={{ marginBottom: token.marginSM, marginTop: token.marginSM }}
				>
					<Check size={16} />
					<Text>{t(feature)}</Text>
				</Flex>
			))}
		</div>
	);
});

const TemplateCard: React.FC<TemplateType> = React.memo(
	({ icon: Icon, title, description, onClick, showAlert, alertMessage }) => {
		const { token } = theme.useToken();
		return (
			<Card
				size="small"
				styles={{
					body: {
						padding: token.padding,
					},
				}}
				style={{
					marginBottom: token.marginSM,
					cursor: 'pointer',
				}}
				onClick={onClick}
				tabIndex={0}
				role="button"
				data-testid={`template-card-${title}`}
				data-analytics-id={getAnalyticsIdForTemplateCard(title)}
			>
				<Row align="middle">
					<Col span={2}>
						<Avatar
							size={48}
							icon={<Icon size={TEMPLATE_ICON_SIZE} />}
							style={{ backgroundColor: colorPalette.neutral2 }}
						/>
					</Col>
					<Col span={22}>
						<Row style={{ marginLeft: token.marginSM }}>
							<Col span={24}>
								<Text strong style={{ fontSize: token.fontSizeLG, fontWeight: 700 }}>
									{title}
								</Text>
							</Col>
							<Col>
								<Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
									{description}
								</Text>
							</Col>
						</Row>
					</Col>
				</Row>
				{showAlert && alertMessage && (
					<Alert
						type="info"
						message={alertMessage}
						showIcon
						style={{
							marginTop: token.marginSM,
						}}
					/>
				)}
			</Card>
		);
	}
);

type CreateDocumentTemplateModalBodyProps = {
	app: PiletApi;
	onClose: () => void;
};

export const CreateDocumentTemplateModalBody = ({
	app,
	onClose,
}: CreateDocumentTemplateModalBodyProps) => {
	const { token } = theme.useToken();

	const { createDocumentTemplate, entitlements } = useAppStore(state => ({
		createDocumentTemplate: state.createDocumentTemplate,
		entitlements: state.entitlements,
	}));

	const handleCreatePDFTemplate = () => {
		setTimeout(() => {
			openCreateTemplateDrawer(app);
		}, 100);
		onClose();
	};

	const openUploadDocxModal = () => {
		setTimeout(() => {
			app.showModal(DocGenExtensionSlots.UPLOAD_DOCX_FILE_MODAL);
		}, 100);
		onClose();
	};

	const handleCreateEditableWordTemplate = () => {
		createDocumentTemplate(DocumentTemplateTypes.Dynamic.type);
		openUploadDocxModal();
	};

	const templateCardHandlers: Record<string, () => void> = {
		handleCreatePDFTemplate,
		handleCreateEditableWordTemplate,
	};
	const templateTypes = getTemplateTypes(entitlements);

	return (
		<>
			<Text>{t('createDocumentTemplateModal.description')}</Text>
			<FeatureList />

			<Flex gap={token.marginSM} vertical>
				<Text strong>{t('createDocumentTemplateModal.templates.title')}</Text>
				<div>
					{templateTypes
						.filter(item => item.isAvailable)
						.map(item => (
							<TemplateCard
								key={item.title}
								icon={item.icon}
								title={item.title}
								description={item.description(token)}
								onClick={templateCardHandlers[item.onClick]}
								showAlert={item.showAlert}
								alertMessage={item.getAlertMessage ? item.getAlertMessage() : undefined}
							/>
						))}
				</div>
			</Flex>
		</>
	);
};
